<template>
  <div class="inline-task-status-list" :class="{ compact: hideTitle }" v-if="tasksToShow.length > 0">
    <h3 v-if="!hideTitle" class="section-title">{{ $t('taskInline.title') }}</h3>
    <div class="tasks">
      <TranslationTaskItem
        v-for="task in tasksToShow"
        :key="task.id"
        :task="task"
        @download="onDownload"
        @retry="onRetry"
        @delete="onDelete"
      />
    </div>
  </div>
  <div v-else class="inline-task-status-list empty"></div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import TranslationTaskItem from '~/components/shared/TranslationTaskItem.vue'
import { useTranslationStore } from '~/stores/translation'
import type { Task } from '~/types/translation'
import { useSecureDownload } from '~/composables/useSecureDownload'

const props = withDefaults(defineProps<{
  hideTitle?: boolean
  category?: string
}>(), {
  hideTitle: false,
  category: 'general'
})

const emit = defineEmits<{
  (e: 'run-in-background'): void
}>()

const translationStore = useTranslationStore()
const { downloadByTask } = useSecureDownload()

// 优先显示进行中的任务，否则显示最近任务；最多显示3条
const tasksToShow = computed<Task[]>(() => {
  const targetCategory = props.category || 'general'
  
  // 先按分类筛选
  const categoryTasks = translationStore.tasks.filter(t => t.category === targetCategory)
  
  const active = categoryTasks.filter(t => t.status === 'uploading' || t.status === 'translating')
  
  if (active.length > 0) return active.slice(0, 3)
  return categoryTasks.slice(0, 3)
})

function onDownload(task: Task) {
  downloadByTask(task).catch(() => {})
}

function onRetry(taskId: string) {
  translationStore.retryTask(taskId)
}

function onDelete(taskId: string) {
  // 简易本地删除（不影响后端记录）
  translationStore.$patch((state) => {
    state.tasks = state.tasks.filter(t => t.id !== taskId)
  })
}
</script>

<style scoped lang="scss">
.inline-task-status-list {
  margin-top: 1rem;
}
.inline-task-status-list.compact {
  margin-top: 0;
}
.section-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: $gray-800;
  margin: 0 0 $spacing-sm 0;
}
.tasks {
  display: flex;
  flex-direction: column;
  gap: $spacing-sm;
}
</style>
